import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MailIcon, MessageCircleIcon, PhoneIcon, XIcon } from 'lucide-react';
import { EnquiryForm } from './EnquiryForm';
import { contact } from '../../data/contact';
import type { EnquiryConfig } from '../../contexts/EnquiryModalContext';
interface EnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: EnquiryConfig;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function EnquiryModal({
  isOpen,
  onClose,
  config
}: EnquiryModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const {
      overflow
    } = document.body.style;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    }, 50);
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-373" data-mp="2bax79f2fpo54">
      {isOpen ? <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" data-id="element-374" data-mp="237d2ndz3rbft">
          <motion.div className="absolute inset-0 bg-ink/50" onClick={onClose} aria-hidden="true" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-375" data-mp="1z7bapwj65wdq" />
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="enquiry-modal-title" className="relative flex max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-[28px] bg-white shadow-lift sm:rounded-[28px]" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease
      }} data-id="element-376" data-mp="2m67gmkqqs7ux">
            <div className="flex items-start justify-between gap-6 border-b border-line px-6 pb-5 pt-6 md:px-10 md:pt-8" data-id="element-377" data-mp="3kh5kduztahh6">
              <div data-id="element-378" data-mp="3ohz5yhps7k6w">
                <h2 id="enquiry-modal-title" className="type-h4 text-ink" data-id="element-379" data-mp="3oihae1mll4pw">
                  {config.title}
                </h2>
                <p className="mt-2 max-w-xl type-card-desc text-body" data-id="element-380" data-mp="3filvblpx8pkl">{config.description}</p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close enquiry form" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-mist text-ink transition-colors duration-150 hover:bg-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-381" data-mp="30ty23m3jsrxt">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-382" data-mp="2wpq93cq24pts" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6 md:px-10 md:py-8" data-id="element-383" data-mp="2mtx208v3xoje">
              <EnquiryForm submitLabel={config.submitLabel} defaultCourse={config.defaultCourse} defaultLocation={config.defaultLocation} showCourseFields={config.showCourseFields} messageLabel={config.messageLabel} data-id="element-384" data-mp="2mt53ap67raow" />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-line bg-cream px-6 py-4 md:px-10" data-id="element-385" data-mp="3bfxwnmkst52x">
              <span className="type-small font-bold text-ink" data-id="element-386" data-mp="36hwc0x7q4xpc">Prefer to talk now?</span>
              <a href={contact.whatsappLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 type-small font-semibold text-ink hover:text-brand-press" data-id="element-387" data-mp="32f00trkik3vf">
                <MessageCircleIcon className="h-4 w-4 text-brand-press" aria-hidden="true" data-id="element-388" data-mp="2shbvk7pa7dz0" />
                WhatsApp
              </a>
              <a href={contact.phoneLink} className="inline-flex items-center gap-2 type-small font-semibold text-ink hover:text-brand-press" data-id="element-389" data-mp="vnh4nng6ubht">
                <PhoneIcon className="h-4 w-4 text-brand-press" aria-hidden="true" data-id="element-390" data-mp="qnw10zq2peyr" />
                {contact.phoneDisplay}
              </a>
              <a href={contact.emailLink} className="inline-flex items-center gap-2 type-small font-semibold text-ink hover:text-brand-press" data-id="element-391" data-mp="1379jvz12l84j">
                <MailIcon className="h-4 w-4 text-brand-press" aria-hidden="true" data-id="element-392" data-mp="1814brpqtwtep" />
                {contact.email}
              </a>
            </div>
          </motion.div>
        </div> : null}
    </AnimatePresence>;
}